import { useMemo } from 'react'
import useSourcelabForm from '@alphabait/ui/form'
import { AlphaTypes } from '../custom-types'
import { renderCustomField } from '../render-custom-field'
import { DndQuestionnaire } from './dnd-questionnaire'

// Stable empty object reference to prevent infinite render loops
// This object is created once and reused across all renders
const EMPTY_FORM_VALUES = {}

interface AlphaFormBuilderProps {
  fields: AlphaTypes[]
  dndOptions?: {
    customWrapper: React.FC<{
      field: AlphaTypes
      children: React.ReactNode
    }>
    dndQuestionnaire: typeof DndQuestionnaire
    setFields: (fields: AlphaTypes[]) => void
  }
  campaignId?: string
}

/**
 * AlphaFormBuilder - A form builder component that integrates with the ui-components
 * form module while providing drag-and-drop functionality for creating questionnaires.
 *
 * This component uses the @alphabait/ui/form module for form rendering and validation,
 * while adding custom field rendering and drag-and-drop capabilities.
 */
export default function AlphaFormBuilder({
  fields,
  dndOptions,
  campaignId,
}: AlphaFormBuilderProps) {
  // Extract customWrapper to use as dependency instead of the whole dndOptions object
  const CustomWrapper = dndOptions?.customWrapper

  // Custom render function that wraps the field rendering
  // Memoized to prevent re-creation on every render
  // Only depends on CustomWrapper, not the entire dndOptions object
  const customCompose = useMemo(
    () => (field: AlphaTypes, formProps: any) => {
      if (CustomWrapper) {
        return (
          <CustomWrapper field={field}>
            {renderCustomField(field, formProps)}
          </CustomWrapper>
        )
      }

      return renderCustomField(field, formProps)
    },
    [CustomWrapper],
  )

  // Use the form hook from ui-components with custom rendering
  const { render, reset } = useSourcelabForm({
    values: EMPTY_FORM_VALUES,
    customRender: customCompose,
    mode: 'onBlur',
    resetAfterSubmit: false,
  })

  // If DnD options are provided, use the DnD questionnaire wrapper
  if (dndOptions) {
    const { dndQuestionnaire, setFields } = dndOptions
    return dndQuestionnaire({
      render,
      reset,
      values: fields,
      setFields,
      campaignId,
    })
  }

  // Otherwise, render the form normally
  return <div>{render(fields)}</div>
}
